import React, { useState, useEffect, useCallback } from 'react';
import {
  BuildingStorefrontIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { Card, Button, Spinner, Modal, SearchInput, Badge } from '../../components/ui';
import { sysadminService } from '../../services/sysadmin.service';
import type { Pharmacy, CreatePharmacyData } from '../../types';
import { toast } from 'react-toastify';

const emptyForm: CreatePharmacyData = {
  name: '',
  code: '',
  isActive: true,
  supportsRefrigerated: false,
  mdiPharmacyId: '',
  description: '',
};

export const Pharmacies: React.FC = () => {
  const [pharmacies, setPharmacies] = useState<Pharmacy[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selected, setSelected] = useState<Pharmacy | null>(null);
  const [form, setForm] = useState<CreatePharmacyData>(emptyForm);
  const [isSaving, setIsSaving] = useState(false);

  const fetchPharmacies = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await sysadminService.getPharmacies();
      setPharmacies(Array.isArray(data) ? data : []);
    } catch {
      toast.error('Failed to load pharmacies');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPharmacies();
  }, [fetchPharmacies]);

  const filtered = pharmacies.filter(
    (p) =>
      p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAdd = () => {
    setSelected(null);
    setForm(emptyForm);
    setIsModalOpen(true);
  };

  const handleEdit = (pharmacy: Pharmacy) => {
    setSelected(pharmacy);
    setForm({
      name: pharmacy.name,
      code: pharmacy.code,
      isActive: pharmacy.isActive,
      supportsRefrigerated: pharmacy.supportsRefrigerated,
      mdiPharmacyId: pharmacy.mdiPharmacyId || '',
      description: pharmacy.description || '',
    });
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.name?.trim() || !form.code?.trim()) {
      toast.error('Name and code are required');
      return;
    }

    const payload: CreatePharmacyData = {
      ...form,
      mdiPharmacyId: form.mdiPharmacyId?.trim() || null,
      description: form.description?.trim() || null,
    };

    setIsSaving(true);
    try {
      if (selected) {
        await sysadminService.updatePharmacy(selected.id, payload);
        toast.success('Pharmacy updated');
      } else {
        await sysadminService.createPharmacy(payload);
        toast.success('Pharmacy created');
      }
      setIsModalOpen(false);
      fetchPharmacies();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to save pharmacy');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (pharmacy: Pharmacy) => {
    if (!window.confirm(`Delete pharmacy "${pharmacy.name}"? This cannot be undone.`)) return;
    try {
      await sysadminService.deletePharmacy(pharmacy.id);
      toast.success('Pharmacy deleted');
      fetchPharmacies();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to delete pharmacy');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-display font-semibold text-secondary-900 dark:text-secondary-50">
            Pharmacies
          </h1>
          <p className="text-secondary-500 dark:text-secondary-400">
            Manage fulfillment pharmacies available to medications
          </p>
        </div>
        <Button onClick={handleAdd}>
          <PlusIcon className="w-4 h-4 mr-1" />
          Add Pharmacy
        </Button>
      </div>

      {/* Content */}
      <Card>
        <div className="p-6">
          <div className="mb-6">
            <SearchInput
              placeholder="Search pharmacies..."
              value={searchTerm}
              onChange={setSearchTerm}
            />
          </div>

          {filtered.length === 0 ? (
            <div className="text-center py-12">
              <BuildingStorefrontIcon className="mx-auto h-12 w-12 text-secondary-400" />
              <h3 className="mt-2 text-sm font-medium text-secondary-900 dark:text-secondary-50">No pharmacies found</h3>
              <p className="mt-1 text-sm text-secondary-500">
                {searchTerm ? 'Try adjusting your search.' : 'Get started by adding a pharmacy.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-secondary-200 dark:border-secondary-700">
                    <th className="text-left py-3 px-4 text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Name</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Code</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Status</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Refrigerated</th>
                    <th className="text-right py-3 px-4 text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-secondary-200 dark:divide-secondary-700">
                  {filtered.map((pharmacy) => (
                    <tr key={pharmacy.id} className="hover:bg-secondary-50 dark:hover:bg-secondary-800/50">
                      <td className="py-3 px-4">
                        <span className="text-sm font-medium text-secondary-900 dark:text-secondary-50">{pharmacy.name}</span>
                        {pharmacy.description && (
                          <p className="text-xs text-secondary-500">{pharmacy.description}</p>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <code className="text-xs text-secondary-600 dark:text-secondary-300">{pharmacy.code}</code>
                      </td>
                      <td className="py-3 px-4">
                        <Badge variant={pharmacy.isActive ? 'success' : 'secondary'} size="sm">
                          {pharmacy.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td className="py-3 px-4">
                        {pharmacy.supportsRefrigerated ? (
                          <Badge variant="accent" size="sm">Supported</Badge>
                        ) : (
                          <span className="text-sm text-secondary-400">—</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button variant="secondary" size="sm" onClick={() => handleEdit(pharmacy)}>
                            <PencilIcon className="w-4 h-4 mr-1" />
                            Edit
                          </Button>
                          <Button variant="danger" size="sm" onClick={() => handleDelete(pharmacy)}>
                            <TrashIcon className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>

      {/* Add/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={selected ? 'Edit Pharmacy' : 'Add Pharmacy'}
        size="lg"
      >
        <div className="space-y-4 p-1">
          <div>
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">Name</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-secondary-50 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
              Integration Code
            </label>
            <input
              type="text"
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
              placeholder="e.g. curexa, pcp_health"
              disabled={!!selected}
              className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-secondary-50 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:opacity-60"
            />
            <p className="mt-1 text-xs text-secondary-500">
              Drives which integration adapter processes orders. Cannot be changed after creation.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
              MDI Pharmacy ID <span className="text-secondary-400">(optional)</span>
            </label>
            <input
              type="text"
              value={form.mdiPharmacyId || ''}
              onChange={(e) => setForm({ ...form, mdiPharmacyId: e.target.value })}
              placeholder="e.g. 13012"
              className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-secondary-50 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            <p className="mt-1 text-xs text-secondary-500">
              MDI-side routing id. Leave blank for pharmacies we submit to directly.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
              Description <span className="text-secondary-400">(optional)</span>
            </label>
            <input
              type="text"
              value={form.description || ''}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-secondary-50 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 text-sm text-secondary-700 dark:text-secondary-300">
              <input
                type="checkbox"
                checked={!!form.isActive}
                onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                className="rounded border-secondary-300 text-primary-500 focus:ring-primary-500"
              />
              Active
            </label>
            <label className="flex items-center gap-2 text-sm text-secondary-700 dark:text-secondary-300">
              <input
                type="checkbox"
                checked={!!form.supportsRefrigerated}
                onChange={(e) => setForm({ ...form, supportsRefrigerated: e.target.checked })}
                className="rounded border-secondary-300 text-primary-500 focus:ring-primary-500"
              />
              Supports refrigerated shipping
            </label>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-secondary-200 dark:border-secondary-700">
            <Button variant="secondary" onClick={() => setIsModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving ? 'Saving...' : selected ? 'Update' : 'Create'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Pharmacies;
